<script setup lang="ts">
import { Link, router, usePage } from '@inertiajs/vue3'
import { Bell, X } from 'lucide-vue-next'
import { computed, onMounted, onUnmounted, ref } from 'vue'

import RelativeTime from '@/components/RelativeTime.vue'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Switch } from '@/components/ui/switch'
import { usePushNotifications } from '@/composables/usePushNotifications'
import { NOTIFICATION_POLL_INTERVAL } from '@/lib/constants'
import type { Auth, Notification } from '@/types'

const props = defineProps<{
  notificationsPath: string
  readPath: (id: number) => string
  readAllPath: string
  pollPath: string
  deleteNotificationPath?: (id: number) => string
  pushSubscribePath?: string
  pushUnsubscribePath?: string
  autoSubscribePush?: boolean
}>()

const page = usePage()
const auth = computed(() => page.props.auth as Auth)

const push = props.pushSubscribePath && props.pushUnsubscribePath
  ? usePushNotifications(props.pushSubscribePath, props.pushUnsubscribePath, { autoSubscribe: props.autoSubscribePush })
  : null

const pushChecked = computed({
  get: () => push?.isSubscribed.value ?? false,
  set: (v: boolean) => v ? push?.subscribe() : push?.unsubscribe(),
})

// Local state — seeded from shared props, kept fresh by polling
const polledUnreadCount = ref<number | null>(null)
const polledNotifications = ref<Notification[] | null>(null)

const unreadCount = computed(() => polledUnreadCount.value ?? auth.value.unread_notifications_count ?? 0)
const notifications = computed<Notification[]>(() => polledNotifications.value ?? auth.value.recent_notifications ?? [])
const hasUnread = computed(() => unreadCount.value > 0)

async function poll() {
  try {
    const res = await fetch(props.pollPath, { headers: { Accept: 'application/json' } })
    if (!res.ok) return
    const data = await res.json()
    polledUnreadCount.value = data.unread_count
    polledNotifications.value = data.notifications
  } catch {
    // silently ignore — network blip or session expired
  }
}

let timer: ReturnType<typeof setInterval> | null = null
onMounted(() => { timer = setInterval(poll, NOTIFICATION_POLL_INTERVAL) })
onUnmounted(() => { if (timer) clearInterval(timer) })

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

async function markRead(n: Notification) {
  if (!n.read_at) {
    // Optimistic update
    if (polledNotifications.value) {
      const idx = polledNotifications.value.findIndex((x) => x.id === n.id)
      if (idx !== -1) polledNotifications.value[idx] = { ...polledNotifications.value[idx], read_at: new Date().toISOString() }
    }
    if (polledUnreadCount.value !== null && polledUnreadCount.value > 0) polledUnreadCount.value--

    await fetch(props.readPath(n.id), {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': csrfToken() },
    }).catch(() => {})
  }

  if (n.action_url) window.location.href = n.action_url
  else window.location.href = props.notificationsPath
}

function deleteNotification(n: Notification) {
  if (!props.deleteNotificationPath) return
  if (polledNotifications.value) {
    polledNotifications.value = polledNotifications.value.filter(x => x.id !== n.id)
    if (!n.read_at && polledUnreadCount.value !== null && polledUnreadCount.value > 0) polledUnreadCount.value--
  }
  router.delete(props.deleteNotificationPath(n.id), { preserveScroll: true })
}

function markAllRead() {
  // Optimistic update
  if (polledNotifications.value) {
    const now = new Date().toISOString()
    polledNotifications.value = polledNotifications.value.map((n) => ({ ...n, read_at: n.read_at ?? now }))
  }
  polledUnreadCount.value = 0

  router.patch(props.readAllPath, {}, { preserveScroll: true })
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button variant="ghost" size="icon" class="relative size-8 md:size-9">
        <Bell class="size-4" />
        <span
          v-if="hasUnread"
          class="bg-destructive text-white absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none"
        >
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </span>
        <span class="sr-only">Notifications</span>
      </Button>
    </DropdownMenuTrigger>

    <DropdownMenuContent align="end" class="w-[min(20rem,calc(100vw-1rem))] p-0">
      <!-- Header -->
      <div class="flex items-center justify-between border-b px-4 py-3">
        <p class="text-sm font-semibold">Notifications</p>
        <button
          v-if="hasUnread"
          class="text-muted-foreground hover:text-foreground text-xs transition-colors"
          @click="markAllRead"
        >
          Mark all read
        </button>
      </div>

      <!-- Notification list -->
      <div class="max-h-80 overflow-y-auto">
        <div
          v-if="notifications.length === 0"
          class="text-muted-foreground flex flex-col items-center gap-2 py-8 text-sm"
        >
          <Bell class="size-6 opacity-30" />
          <p>No notifications yet</p>
        </div>

        <button
          v-for="n in notifications"
          :key="n.id"
          class="hover:bg-muted/60 flex w-full cursor-pointer items-start gap-3 border-b px-4 py-3 text-left transition-colors last:border-b-0"
          :class="{ 'bg-primary/5': !n.read_at }"
          @click="markRead(n)"
        >
          <span
            class="mt-1.5 size-2 shrink-0 rounded-full"
            :class="n.read_at ? 'bg-transparent' : 'bg-primary'"
          />
          <div class="min-w-0 flex-1">
            <p class="text-sm leading-snug" :class="n.read_at ? 'text-muted-foreground' : 'font-medium'">
              {{ n.title }}
            </p>
            <p v-if="n.body" class="text-muted-foreground mt-0.5 truncate text-xs">{{ n.body }}</p>
            <RelativeTime :date="n.created_at" class="text-muted-foreground mt-1 block text-xs" />
          </div>
          <button
            v-if="deleteNotificationPath"
            class="text-muted-foreground hover:text-destructive -mr-1 ml-1 shrink-0 rounded p-0.5 transition-colors"
            @click.stop="deleteNotification(n)"
          >
            <X class="size-3.5" />
          </button>
        </button>
      </div>

      <!-- Footer -->
      <div class="border-t px-4 py-2.5 flex items-center justify-between gap-2">
        <Link
          :href="notificationsPath"
          class="text-muted-foreground hover:text-foreground text-xs transition-colors"
        >
          See all notifications
        </Link>
        <label
          v-if="push?.isSupported.value"
          class="flex items-center gap-1.5 cursor-pointer select-none"
          :class="push.isLoading.value ? 'opacity-50 pointer-events-none' : ''"
        >
          <span class="text-muted-foreground text-xs">Push</span>
          <Switch v-model="pushChecked" />
        </label>
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
